import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { CopyButton, Modal, DecisionDialog } from "../../common";
import { ClientDetailsForm } from "../../forms/invoice";
import { setVideoAskUrl } from "../../../store/reducers/ui";
import { Invoice } from "../../../types/InvoiceTypes";
import { toastSuccess } from "../../../utils/toasts";

type Props = {
  side: "from" | "to";
  invoice: Invoice;
  isOpen: boolean;
  onClose: () => void;
};

export const InvoiceDetailsDataModal: FC<Props> = ({
  side,
  invoice,
  isOpen,
  onClose,
}) => {
  const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);
  const [formHasChanged, setFormHasChanged] = useState(false);
  const dispatch = useDispatch();

  const { label, title, subtitle } = useMemo(
    () => getComponentValues(side, invoice),
    [side, invoice]
  );

  const onSuccess = useCallback(() => {
    onClose();
    toastSuccess(`${label} added`);
  }, [onClose, label]);

  const requestClose = useCallback(() => {
    if (formHasChanged) {
      setShowConfirmCloseModal(true);
      return;
    }

    onClose();
  }, [formHasChanged, onClose]);

  // Video guide
  useEffect(() => {
    if (isOpen && side === "from") {
      dispatch(setVideoAskUrl("https://www.videoask.com/f57hekf6s"));
    }

    return () => {
      dispatch(setVideoAskUrl(null));
    };
  }, [dispatch, isOpen, side]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        close={requestClose}
        title={title}
        subtitle={subtitle}
      >
        <ClientDetailsForm
          side={side}
          invoice={invoice}
          onSuccess={onSuccess}
          onChange={() => !formHasChanged && setFormHasChanged(true)}
        />
      </Modal>

      <DecisionDialog
        isOpen={showConfirmCloseModal}
        title="Are you sure you want to leave?"
        cancel={{
          label: "No, back to edit details",
          onClick: () => setShowConfirmCloseModal(false),
        }}
        proceed={{
          label: "Yes, leave",
          onClick: onClose,
        }}
      >
        The progress will be lost
      </DecisionDialog>
    </>
  );
};

const getComponentValues = (side: "from" | "to", invoice: Invoice) => {
  const { user_is_issuer, user_is_payer } = invoice;

  const isUserData =
    (side === "from" && user_is_issuer) || (side === "to" && user_is_payer);

  const label = isUserData
    ? "Your details"
    : user_is_issuer
      ? "Client details"
      : "Recipient details";

  const title =
    isUserData || !user_is_issuer ? (
      label
    ) : (
      <div className="flex justify-between">
        {label}
        <CopyMessageButton />
      </div>
    );

  const subtitle = isUserData
    ? "Fill out your invoicing details"
    : user_is_issuer
      ? "Fill up your client business details"
      : "Fill up your recipient details";

  return { label, title, subtitle };
};

const CopyMessageButton = () => {
  const message = [
    "Hello, to fill in the invoice for services I provide, could you please share the following details about your business?",
    "",
    "1. Registered business name",
    "2. Registration number (It might be the same as your Tax ID in some places.)",
    "3. Tax ID",
    "4. Address",
    "5. Postal code",
    "6. City",
    "7. Country",
  ].join("\n");

  return (
    <CopyButton
      contentClassName="flex gap-3 items-center font-poppins font-bold text-blue-500"
      value={message}
    >
      Copy
    </CopyButton>
  );
};
